<script setup>
import {
  LMap,
  LTileLayer,
  LImageOverlay,
  LControlZoom,
  LMarker,
  LTooltip,
} from "@vue-leaflet/vue-leaflet";
import "leaflet/dist/leaflet.css";
import { useRangePinsView } from "@/scripts/Utilities/RangePinsView.js";
import SearchableSelect from "@/components/SearchableSelectComponent.vue";
import ImportRangePinsModal from "@/components/Dashboard/Utilities/RangePins/ImportRangePinsModal.vue";
import ClearRangePinsModal from "@/components/Dashboard/Utilities/RangePins/ClearRangePinsModal.vue";

const {
  myMap,
  selectedCourse,
  rangePins,
  loading,
  showCourseMap,
  zoom,
  center,
  url,
  opacity,
  mapImage,
  bounds,
  icon,
  targetCourse,
  drivingRangeSetup,
  courses,
  showImportModal,
  showClearModal,
  enableSaveRangePins,
  openImportRangePins,
  // openClearRangePins,
  confirmClearRangePins,
  centerUpdated,
  changeView,
  updatePin,
  updateRangePins,
  applyMapView,
} = useRangePinsView();
</script>

<template>
  <div>
    <div class="content-header">
      <div class="container-fluid">
        <div class="row mb-2">
          <div class="col-sm-6">
            <h1 class="m-0">Driving Range pins:</h1>
          </div>
          <div class="col-sm-6">
            <ol class="breadcrumb float-sm-right">
              <li class="breadcrumb-item"><a href="#">Dashboard</a></li>
              <li class="breadcrumb-item active">Devices</li>
            </ol>
          </div>
        </div>
      </div>
    </div>

    <section class="content">
      <div class="container-fluid">
        <div class="card" id="course-config">
          <div class="card-body">
            <div class="box-body table-responsive no-padding">
              <div class="row">
                <div class="col-9">
                  <LMap
                    style="height: 650px; border-radius: 5px"
                    ref="myMap"
                    @update:center="centerUpdated"
                    :zoom="zoom"
                    :center="center"
                    :options="{
                      zoomControl: false,
                      attributionControl: false,
                      maxBounds: bounds.every((coord) =>
                        coord.every((val) => val !== 0)
                      )
                        ? bounds
                        : undefined,
                    }"
                  >
                    <LTileLayer :url="url"></LTileLayer>

                    <LImageOverlay
                      v-if="mapImage && showCourseMap"
                      :opacity="opacity"
                      :url="mapImage"
                      :bounds="bounds"
                    ></LImageOverlay>

                    <LControlZoom position="topright"></LControlZoom>

                    <div v-if="rangePins.length && targetCourse">
                      <template
                        v-for="(pin, i) in rangePins"
                        :key="pin.key || i"
                      >
                        <LMarker
                          v-if="Array.isArray(pin.point)"
                          :lat-lng="pin.point"
                          :draggable="true"
                          @update:lat-lng="updatePin(i, $event)"
                          :icon="icon"
                        >
                          <LTooltip>{{ pin.name || `Pin ${i + 1}` }}</LTooltip>
                        </LMarker>
                      </template>
                    </div>
                  </LMap>
                </div>
                <div class="col-3">
                  <div class="col-12 text-center">
                    <div class="btn-group">
                      <button
                        @click="changeView(false)"
                        type="button"
                        class="btn btn-sm"
                        :class="{
                          'btn-primary': showCourseMap,
                          'btn-default': !showCourseMap,
                        }"
                      >
                        TM Map
                      </button>
                      <button
                        @click="changeView(true)"
                        type="button"
                        class="btn btn-sm"
                        :class="{
                          'btn-primary': !showCourseMap,
                          'btn-default': showCourseMap,
                        }"
                      >
                        Satellite
                      </button>
                    </div>
                    <hr />
                  </div>

                  <div class="col-12">
                    <div class="form-group">
                      <label>Select course:</label>
                      <SearchableSelect
                        v-model="targetCourse"
                        :options="courses"
                        :label="'name'"
                        :trackBy="'id'"
                        :placeholder="'Select the courses'"
                      />
                    </div>
                  </div>

                  <div class="col-12 d-flex align-items-start flex-column">
                    <button
                      class="btn btn-sm btn-info mt-2 mb-2"
                      @click="openImportRangePins"
                      :disabled="!targetCourse"
                    >
                      Import Range Pins
                    </button>

                    <button
                      class="btn btn-sm btn-info mb-2"
                      @click="applyMapView"
                      v-if="drivingRangeSetup && targetCourse"
                    >
                      Reset Map View
                    </button>
                    <!-- <button
                      v-if="targetCourse"
                      class="btn btn-danger mt-2"
                      @click="openClearRangePins"
                    >
                      Clear Range Pins
                    </button> -->
                  </div>

                  <div
                    class="col-12"
                    v-show="
                      selectedCourse != null && rangePins.length && targetCourse
                    "
                  >
                    <div class="form-group">
                      <label>Imported from:</label>
                      <div class="border border-secondary border-2 rounded p-2">
                        Range Pins
                      </div>
                    </div>
                  </div>

                  <div v-if="rangePins.length && targetCourse">
                    <hr />
                    <div class="form-group row">
                      <button
                        @click="updateRangePins"
                        :disabled="!enableSaveRangePins"
                        :class="{
                          'btn btn-primary': enableSaveRangePins,
                          'btn btn-secondary': !enableSaveRangePins,
                        }"
                        :style="{
                          width: '100%',
                          cursor: enableSaveRangePins
                            ? 'pointer'
                            : 'not-allowed',
                        }"
                        style="width: 100%"
                      >
                        Update Pins
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="overlay" v-show="loading">
            <i class="fas fa-spinner fa-spin fa-lg"></i>
          </div>
        </div>
      </div>
    </section>

    <ImportRangePinsModal v-model="showImportModal" :courses="courses" />

    <ClearRangePinsModal
      v-model="showClearModal"
      :loading="loading"
      @confirm="confirmClearRangePins"
    />
  </div>
</template>

<style scoped>
.overlay {
  z-index: 1000 !important;
  background-color: rgba(27, 30, 33, 0.7) !important;
}

.overlay i {
  color: white !important;
}

.import {
  border-radius: 0 3px 3px 0 !important;
}

/* Hide any Leaflet attribution */
:deep(.leaflet-control-attribution) {
  display: none !important;
}
</style>
